<template>
  <div class="bi-summary">
    <div class="__header">
      <div class="__title-container">
        <div class="__title">{{ $t('page.business-intelligence.title') }}</div>
        <BaseButton isSquare hasIconLeft isSmall isGrey @click.native="navigateToAdminTenant()">
          <BaseIcon name="edit-2" />
          {{ $t('page.business-intelligence.edit-alias') }}</BaseButton
        >
      </div>
      <BaseDatePicker
        variant="full"
        @on-save-dates="updateDateRange"
        :startDate="dates[0]"
        :endDate="dates[1]"
        :datePickerValues="datePickerPeriod"
      />
    </div>
    <div class="__content">
      <BISummarySection :data="performanceSummary" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { bus } from '@/main';
import { formatStandardDate, toMoment } from '@/util/date-util';
import BaseDatePicker from '@/components/base/BaseDatePicker.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BISummarySection from './BISummarySection.vue';
import { BI_DATE_PICKER_PERIOD } from '@/util/constants/date-picker-periods';

export default {
  name: 'BISummary',
  components: {
    BaseDatePicker,
    BISummarySection,
    BaseButton,
    BaseIcon,
  },
  props: {
    dates: {
      type: Array,
      required: true,
    },
  },
  watch: {
    dates() {
      this.getPerformanceSummary({ startDate: this.dates[0], endDate: this.dates[1] });
    },
  },
  computed: {
    ...mapGetters('biDashboard', ['performanceSummary']),
    awardsData() {
      // display a straight line if no data or less than 2 data points
      if (this.performanceSummary.current_period.history.length === 0) {
        return [0, 0];
      }
      if (this.performanceSummary.current_period.history.length === 1) {
        return [
          this.performanceSummary.current_period.history[0].amount,
          this.performanceSummary.current_period.history[0].amount,
        ];
      }
      return this.performanceSummary.current_period.history.map((award) => award.amount);
    },
    datePickerPeriod() {
      return BI_DATE_PICKER_PERIOD;
    },
  },
  methods: {
    ...mapActions('biDashboard', ['getPerformanceSummary']),
    formatStandardDate,
    toMoment,
    updateDateRange(dates) {
      this.$emit('on-date-changed', dates);
    },
    navigateToAdminTenant() {
      this.$router.push({ name: 'admin-tenant', hash: '#other-names' });
    },
  },
  async created() {
    this.getPerformanceSummary({ startDate: this.dates[0], endDate: this.dates[1] });
  },
  async mounted() {
    bus.$on('refresh-bi-dashboard', () => {
      this.getPerformanceSummary({ startDate: this.dates[0], endDate: this.dates[1] });
    });
  },
  async beforeDestroy() {
    bus.$off('refresh-bi-dashboard');
  },
};
</script>

<style lang="scss">
.bi-summary {
  background-color: $color-white;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: $border-radius-root;

  // Phones: less padding, and the date range under the title instead of beside it.
  @include screen-below('tablet') {
    padding: 16px;
  }

  > .__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include screen-below('tablet') {
      flex-wrap: wrap;
      gap: 12px;
    }

    .__title-container {
      display: flex;
      gap: 16px;

      @include screen-below('tablet') {
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      }

      .__title {
        font-size: $font-size-xbigger;
        font-weight: 600;
      }

      .base-icon {
        width: 14px;
      }
    }
  }
  > .__content {
    margin-top: 24px;
  }
}
</style>
